import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import { Box, Input, Text } from '@/ui';

// Keep a comfortable click target even for short titles, and shared padding so
// the preview and the edit input line up.
const MIN_WIDTH = 120;
const PADDING_X = { pl: 2, pr: 4 };

type Props = {
  title: string;
};

export const EditableTitle = ({ title }: Props) => {
  const { setTitle } = useUpdateExperience();
  // null means previewing; the preview reads `title` directly so external
  // updates stay live, and an in-progress draft can never be clobbered.
  const [draft, setDraft] = useState<string | null>(null);
  const previewRef = useRef<HTMLButtonElement>(null);
  const sizerRef = useRef<HTMLDivElement>(null);
  const [inputWidth, setInputWidth] = useState(MIN_WIDTH);

  // Size the edit input to its content so it grows only as needed, rather than
  // filling the header. A hidden sizer mirrors the draft with identical font
  // and padding; add a little room for the caret and border.
  useLayoutEffect(() => {
    if (draft === null || !sizerRef.current) return;
    setInputWidth(Math.max(MIN_WIDTH, sizerRef.current.offsetWidth + 6));
  }, [draft]);

  const exitEditing = (refocusPreview: boolean) => {
    setDraft(null);
    if (refocusPreview) {
      requestAnimationFrame(() => previewRef.current?.focus());
    }
  };

  const commit = (refocusPreview: boolean) => {
    if (draft === null) return;
    const next = draft.trim();
    if (next && next !== title) {
      setTitle(next);
    }
    exitEditing(refocusPreview);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(true);
    } else if (event.key === 'Escape') {
      exitEditing(true);
    }
  };

  if (draft !== null) {
    return (
      <>
        <Input
          aria-label="Page title"
          autoFocus
          fontSize="ah-control"
          fontWeight={600}
          h="28px"
          maxLength={75}
          maxW="100%"
          minW={`${MIN_WIDTH}px`}
          size="sm"
          value={draft}
          width={`${inputWidth}px`}
          onBlur={() => commit(false)}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          onKeyDown={handleKeyDown}
          {...PADDING_X}
        />
        <Box
          aria-hidden
          fontSize="ah-control"
          fontWeight={600}
          left="-9999px"
          position="absolute"
          ref={sizerRef}
          top="0"
          visibility="hidden"
          whiteSpace="pre"
          {...PADDING_X}
        >
          {draft || ' '}
        </Box>
      </>
    );
  }

  return (
    <Text
      _focusVisible={{ boxShadow: 'outline' }}
      _hover={{ bg: 'rgba(0,0,0,0.04)' }}
      aria-label={`Edit title: ${title}`}
      as="button"
      borderRadius="md"
      color="ink.strong"
      fontSize="ah-control"
      fontWeight={600}
      lineHeight={1}
      maxW="100%"
      minW={`${MIN_WIDTH}px`}
      overflow="hidden"
      py={0.5}
      ref={previewRef}
      textAlign="left"
      textOverflow="ellipsis"
      title={title}
      type="button"
      whiteSpace="nowrap"
      onClick={() => setDraft(title)}
      {...PADDING_X}
    >
      {title}
    </Text>
  );
};
